import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { tap } from 'rxjs/operators';

/**
 * Asset caching interceptor
 * 
 * This interceptor caches static assets (SVGs, images, fonts, etc.) in memory
 * to prevent redundant HTTP requests. This is especially important for SVG icons
 * that are loaded via MatIconRegistry.
 * 
 * Benefits:
 * - Reduces network requests for static assets
 * - Improves performance by serving cached assets instantly
 * - Works seamlessly with Angular's HttpClient
 * - Respects browser cache-control headers
 */

// Asset patterns to cache
const CACHEABLE_PATTERNS = [
  /\.svg$/i,           // SVG files
  /\.png$/i,           // PNG images
  /\.jpg$/i,           // JPEG images
  /\.jpeg$/i,          // JPEG images
  /\.gif$/i,           // GIF images
  /\.webp$/i,          // WebP images
  /\.woff2?$/i,        // Web fonts
  /\.ttf$/i,           // TrueType fonts
  /\.eot$/i,           // Embedded OpenType fonts
  /\/assets\//i,       // All assets folder requests
];

// Cache duration in milliseconds (default: 1 hour)
const CACHE_DURATION_MS = 60 * 60 * 1000;

interface CacheEntry {
  response: HttpResponse<any>;
  timestamp: number;
}

// Enhanced cache with timestamps
const enhancedCache = new Map<string, CacheEntry>();

/**
 * Check if a URL should be cached
 */
function shouldCacheUrl(url: string): boolean {
  return CACHEABLE_PATTERNS.some(pattern => pattern.test(url));
}

/**
 * Check if a cached entry is still valid
 */
function isCacheValid(entry: CacheEntry): boolean {
  const age = Date.now() - entry.timestamp;
  return age < CACHE_DURATION_MS;
}

/**
 * Asset caching interceptor function
 */
export const assetCacheInterceptor: HttpInterceptorFn = (req, next) => {
  // Only cache GET requests for static assets
  if (req.method !== 'GET' || !shouldCacheUrl(req.url)) {
    return next(req);
  }

  // Check if we have a valid cached response
  const cachedEntry = enhancedCache.get(req.url);
  if (cachedEntry && isCacheValid(cachedEntry)) {
    // Return cached response immediately
    return new Promise(resolve => {
      // Use setTimeout to make it async and avoid potential issues
      setTimeout(() => resolve(cachedEntry.response.clone()), 0);
    }) as any;
  }

  // If not cached or expired, make the request and cache the response
  return next(req).pipe(
    tap(event => {
      // Only cache successful responses
      if (event instanceof HttpResponse && event.status === 200) {
        enhancedCache.set(req.url, {
          response: event.clone(),
          timestamp: Date.now()
        });
      }
    })
  );
};

/**
 * Clear the asset cache (useful for testing or manual cache invalidation)
 */
export function clearAssetCache(): void {
  enhancedCache.clear();
  console.log('🗑️ Asset cache cleared');
}

/**
 * Get cache statistics
 */
export function getAssetCacheStats(): { size: number; entries: string[] } {
  return {
    size: enhancedCache.size,
    entries: Array.from(enhancedCache.keys())
  };
}
